import { useState, useRef, useEffect, useContext } from "react"

// ** Third Party Components
import { ChevronDown, Plus, Search } from "lucide-react"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Contexts
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface SearchableSelectProps {
  options: any[] // The options array is optional, so it can be passed or defaulted
  selectedOption?: any | null // The currently selected option
  onSelect?: (option: any) => void // Callback to handle option selection
  placeholder?: string // Custom placeholder text
  className?: string // Custom className for the select input
  onAddOption: (option: boolean) => void
}

export default function SearchableSelect({
  options,
  selectedOption = null,
  onSelect,
  onAddOption,
  placeholder = "Select an option",
  className = "", // Default empty string, so it doesn't override if not provided
}: SearchableSelectProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const { projectDefaultContractor, projectDetails } = useContext(UserRoleContext)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [optionsState, setOptions] = useState<any[]>(options)
  const [selected, setSelected] = useState<any | null>(selectedOption)

  const filteredOptions = optionsState.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
      setOptions(options)
    }
  }, [isOpen])

  useEffect(() => {
    if (options) {
      const option = options.find((user) => {
        if (user.id === projectDetails?._contractor_company?.id) return options
      })

      if (onSelect) onSelect(option)

      setSelected(option)
    }
  }, [projectDefaultContractor, options])

  const handleSelect = (option: any) => {
    setSelected(option)
    setIsOpen(false)
    setSearchTerm("")
    if (onSelect) {
      onSelect(option)
    }
  }

  return (
    <div className='w-full'>
      <div ref={containerRef} className='relative'>
        <button
          type='button'
          onClick={() => {
            setIsOpen(!isOpen)
            setSearchTerm("")
          }}
          className={cn(
            "flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-[7px] text-left shadow-sm hover:border-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500",
            className,
          )}
        >
          <span className={cn("text-xs sm:text-sm", selected ? "text-gray-900" : "text-gray-500")}>
            {selected ? selected.label : translate(placeholder)}
          </span>
          <ChevronDown
            size={20}
            className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className='absolute z-10 mt-2 w-full rounded-lg border border-gray-300 bg-white shadow-lg'>
            {/* Search Input */}
            <div className='border-b border-gray-200 p-3'>
              <div className={cn("relative", isHebrew ? "rtl" : "ltr")}>
                <Search
                  size={18}
                  className={cn("absolute top-2.5 text-gray-400", isHebrew ? "right-3" : "left-3")}
                />
                <input
                  ref={searchInputRef}
                  type='text'
                  placeholder={translate("Search options")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={cn(
                    "w-full rounded-md border border-gray-300 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
                    isHebrew ? "pl-3 pr-10" : "pl-10 pr-3",
                  )}
                />
              </div>
            </div>

            {/* Options List */}
            <div className='max-h-64 overflow-y-auto'>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    type='button'
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    className={cn(
                      "w-full px-4 py-2 text-sm transition-colors",
                      selected?.id === option.id
                        ? "bg-blue-100 text-blue-900"
                        : "text-gray-900 hover:bg-gray-100",
                      isHebrew ? "text-right" : "text-left",
                    )}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className='px-4 py-3 text-center text-sm text-gray-500'>
                  {translate("No options found")}
                </div>
              )}
            </div>

            {/* Add New Option Section */}
            <div className='border-t border-gray-200'>
              <button
                type='button'
                onClick={() => onAddOption(true)}
                className='flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-blue-600 transition-colors hover:bg-blue-50'
              >
                <Plus size={18} />
                {translate("Add new option")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
